<script lang="ts">
  import { onMount } from "svelte"
  import { flip } from "svelte/animate"
  import Sortable from "sortablejs"
  import LoadingSpinner from "$app/components/LoadingSpinner.svelte"
  import StatementForm from "$app/Pages/Plans/OnePageView/StatementForm.svelte"
  import StatementHelp from "$app/Pages/Plans/OnePageView/StatementHelp.svelte"
  import SymbolPicker from "$app/Pages/Plans/OnePageView/SymbolPicker.svelte"
  import SymbolUploader from "$app/Pages/Plans/OnePageView/SymbolUploader.svelte"
  import { SquarePen, Trash2, Menu, Plus } from "lucide-svelte"
  import { toast, wait, xhr, sortElementsForSortable } from "$app/utils"
  import Honeybadger from "$app/utils/honeybadger"
  import { STATEMENT_SECTIONS } from "$app/constants"
  import { appStore } from "$app/store.svelte"

  // Used for real time in case there is a channel
  const channel_id = appStore.channel_id

  interface Props {
    section: StatementSectionType
    symbolPicker?: boolean
    initialStatus?: string
    onupdate: (statements: StatementType[]) => void
  }

  let props = $props()
  let {
    section,
    symbolPicker = true,
    initialStatus = "",
    onupdate
  }: Props = props

  let statements = $state(
    appStore.statements
      .filter((st) => st.section_type === section)
      .sort((a, b) => a.position - b.position)
  )

  let isLoading: boolean = $state(false)
  let editStatus: string = $state(initialStatus)

  const sectionD = STATEMENT_SECTIONS[section] || {
    name: "",
    plural: "",
    domId: ""
  }

  const flipDurationMs = 300
  let currentStatement: StatementType = $state(null)

  function isSwot(section: StatementSectionType): boolean {
    return ["Strength", "Weakness", "Opportunity", "Threat"].includes(section)
  }

  let sortableSection = $state<HTMLElement | null>(null)

  let sort: any = null
  function setSortable(): void {
    let timeoutHandler: any = null
    const delay = 20
    let elem: HTMLElement

    let scrollFn: Function

    if (!sortableSection) return
    if (sort) {
      sort.destroy()
    }

    sort = Sortable.create(sortableSection, {
      animation: 200,
      group: "statements",
      scrollSensitivity: 150,
      delay: 400,
      delayOnTouchOnly: true,
      direction: "vertical",
      ghostClass: "opacity-50",
      dragClass: "border-green-400",
      emptyInsertThreshold: 20,
      onEnd: async (e: EventSortType) => {
        if (e.newIndex == e.oldIndex) return
        sort.options.sort = false // Disable sorting while saving
        let newStatements = statements.sort((a, b) => a.position - b.position)

        try {
          await saveSortOrder(
            sortElementsForSortable(newStatements, e.newIndex, e.oldIndex)
          )
        } catch (e) {
        } finally {
          sort.options.sort = true
        }
      },
      onStart: (evt: EventSortType) => {
        const contHeight = evt.srcElement.clientHeight
        const itemHeight = evt.item.clientHeight

        scrollFn = evt.item.ondrag = (mevt: MouseEvent) => {
          if (timeoutHandler == null) {
            timeoutHandler = setTimeout(function () {
              const offsetClientY = mevt.clientY + 20

              if (offsetClientY > contHeight) {
                elem.scrollBy(0, (offsetClientY / 40) * 5)
              } else if (mevt.clientY - itemHeight < 0) {
                elem.scrollBy(
                  0,
                  (Math.abs(mevt.clientY - itemHeight) / 30) * -10
                )
              }
              timeoutHandler = null
            }, delay)
          }
        }
      }
    })

    elem = sort.el
  }

  async function saveSortOrder(newStatements: StatementType[]): Promise<void> {
    try {
      const resp = await xhr.post<StatementType[]>(
        `/plans/${appStore.plan.id}/statements/sort.json?channel=${channel_id}`,
        {
          statement_ids: newStatements.map((st) => st.id),
          section_type: section
        }
      )
      let data = resp.data.sort((a, b) => a.position - b.position)
      onupdate(data)
    } catch (e: any) {
      Honeybadger.notify(e)
      toast("There was an error updating the sort order", "error")
      let tempStatements = [...statements]
      statements = []
      await wait(30)
      statements = tempStatements
    }
  }

  // Delete statement
  async function deleteStatement(statement: StatementType): Promise<void> {
    statement._selected = true
    await wait(50)

    const confMsg = isSwot(statement.section_type)
      ? `Are you sure you want to archive "${statement.name}"?
      You can restore it later.`
      : `Are you sure you want to delete the selected ${sectionD.name}`
    if (!confirm(confMsg)) {
      statement._selected = false
      return
    }

    try {
      let resp = await xhr.delete(
        `/plans/${appStore.plan.id}/statements/${statement.id}?channel=${channel_id}`
      )

      const idx = statements.findIndex((s) => s.id === statement.id)
      statements.splice(idx, 1)

      const msg = isSwot(statement.section_type)
        ? `${sectionD.name} archived. You can restore in the <a href="/plans/${appStore.plan.id}/archive" class="text-blue-700">archive</a>`
        : `${sectionD.name} has been deleted`
      toast(msg, "success")

      appStore.updateStatements(statements, section)
      onupdate(statements)
    } catch (e) {
      Honeybadger.notify(e)
      toast("There was an error deleting the statement", "error")
    } finally {
      statement._selected = false
      statements = statements
    }
  }

  async function updateStatement(statement: StatementType): Promise<void> {
    editStatus = ""

    appStore.updateStatement(statement)

    statements = appStore.statements
      .filter((st) => st.section_type === section)
      .sort((a, b) => a.position - b.position)

    await wait(50)
    setSortable()
  }

  async function createStatement(statement: StatementType): Promise<void> {
    editStatus = ""
    statement._selected = false
    statements = [...statements, statement]

    appStore.trackStatement.edited = false

    appStore.updateStatements(statements, section)

    onupdate(statements)

    await wait(statements.length * 5)
    setSortable()
  }

  async function confirmCancel(): Promise<void> {
    if (
      appStore.trackStatement.edited &&
      appStore.trackStatement.status === "edit"
    ) {
      if (
        confirm("You have unsaved changes, are you sure you want to leave?")
      ) {
        appStore.trackStatement.edited = false
        cancelEdit()
      }
    } else {
      cancelEdit()
    }
  }

  async function cancelEdit(): Promise<void> {
    editStatus = ""
    await wait(statements.length * 5)
    setSortable()
  }

  onMount(async () => {
    isLoading = false
    setSortable()
  })
</script>

<div class="text-center px-2 pt-4">
  <div class="text-left relative">
    <div class:hidden={!isLoading}>
      <LoadingSpinner />
    </div>
    {#if editStatus === "new"}
      <StatementForm
        statement={{} as StatementType}
        title={`New ${sectionD.name}`}
        {section}
        oncancel={confirmCancel}
        oncreate={(data) => createStatement(data)}
      />
    {:else if editStatus === "edit"}
      <StatementForm
        statement={currentStatement}
        title={`Edit ${sectionD.name}`}
        {section}
        oncancel={confirmCancel}
        onupdate={(data) => updateStatement(data)}
        oncreate={(data) => createStatement(data)}
      />
    {:else if editStatus === "selectSymbol"}
      <SymbolPicker
        statement={currentStatement}
        oncancel={() => (editStatus = "")}
        onupdate={(data) => updateStatement(data)}
      />
    {:else if editStatus === "uploadSymbol"}
      <SymbolUploader
        statement={currentStatement}
        oncancel={() => (editStatus = "")}
        onupdate={(data) => updateStatement(data)}
      />
    {:else}
      <div class="inline-flex justify-start items-center mb-3 select-none">
        <h3
          class="text-2xl flex leading-6 font-medium text-gray-900"
          id="modal-headline"
        >
          {sectionD.plural}
        </h3>
        <button
          class="text-purple-500 border-2 border-purple-500 hover:text-white hover:bg-purple-500 rounded-full px-4 py-1 ml-4 text-base flex items-center"
          title="New {sectionD.name}"
          data-name="new-statement"
          onclick={() => (editStatus = "new")}
        >
          <Plus class="mr-1 size-5 inline" />
          New
        </button>
      </div>
      <section
        bind:this={sortableSection}
        style="overflow: auto;height:80vh"
        role="list"
      >
        {#each statements as st (st.id)}
          <div
            animate:flip={{ duration: flipDurationMs }}
            class:bg-blue-50={st._selected}
            class:bg-white={!st._selected}
            class="border mb-4 flex relative item hover:cursor-move"
            data-name="statement-item"
          >
            <div class="absolute right-4 top-2">
              <button
                class="text-gray-500 hover:text-blue-500"
                title="Edit"
                data-name="edit-statement"
                onclick={() => {
                  editStatus = "edit"
                  currentStatement = st
                }}
              >
                <SquarePen class="size-5" />
              </button>
              <button
                class="text-gray-500 ml-3 {isSwot(st.section_type)
                  ? 'hover:text-yellow-700'
                  : 'hover:text-red-500'} "
                title="Delete"
                data-name="delete-statement"
                onclick={() => deleteStatement(st)}
              >
                <Trash2 class="size-5" />
              </button>
            </div>
            <div
              class="px-3 flex items-center justify-center bg-blue-50 hover:bg-blue-100 border-r border-gray-100"
            >
              <Menu class="size-4 text-gray-500 hover:text-gray-900" />
            </div>

            <div
              class="py-4 px-8 grid gap-2 {symbolPicker
                ? 'grid-cols-3'
                : 'grid-cols-2'}"
            >
              {#if symbolPicker}
                <div class="w-full justify-center items-center group">
                  <img src={st.symbol_url} class="mx-auto" alt={st.name} />
                  <div class="text-center mt-4">
                    <button
                      class="bg-purple-500 text-white px-4 mb-4"
                      data-name="replace-symbol"
                      onclick={() => {
                        currentStatement = st
                        editStatus = "selectSymbol"
                      }}
                    >
                      Replace Symbol
                    </button>

                    <button
                      class="text-blue-500"
                      data-name="upload-symbol"
                      onclick={() => {
                        currentStatement = st
                        editStatus = "uploadSymbol"
                      }}
                    >
                      Upload Symbol
                    </button>
                  </div>
                </div>
              {/if}

              <div class="col-span-2 mt-2">
                <h2 class="font-bold pr-8">{st.name}</h2>
                <div class="mt-4 ProseMirror">{@html st.description || ""}</div>
              </div>
            </div>
          </div>
        {:else}
          <StatementHelp {section} />
        {/each}
      </section>
    {/if}
  </div>
</div>
